import {
  Button,
  Card,
  CardImage,
  H5,
  H6,
  InputTextArea,
  InputLengthIndicator,
} from "@sellar-io/components";
import { SupplierDistanceResponseType } from "@sellar-io/api-v2/types";
import { useMemo, useState } from "react";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../components";
import { twMerge } from "tailwind-merge";
import { useSupplierListByDistance } from "../../hooks/api/v2/supplier/useSupplierListByDistance";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../components/modal/Dialog";
import { Alert } from "../../components/alert/Alert";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { useSupplierRelationshipOnboarding } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipOnboarding";
import { v4 } from "uuid";
import { useForm, useWatch } from "react-hook-form";
import { defaultLondonLocation } from "../../components/map/SellarMap.shared";
import { useEvents } from "../../hooks";

type ConnectWithSupplierFormType = {
  connectionComment: string;
  mailingListPermission: boolean;
  accountManager?: string;
};

export const EducateConnectPotentialSuppliers = () => {
  const { business } = useSellarCurrentBusiness();
  const [supplierDistance, setSupplierDistance] = useState(40);

  const suppliersByDistanceQuery = useSupplierListByDistance(
    {
      offset: 0,
      limit: 15,
      lng: business?.Location?.lng ?? defaultLondonLocation.lng,
      lat: business?.Location?.lat ?? defaultLondonLocation.lat,
      distance: supplierDistance,
      orderBy: "random",
      unConnectedOnly: true,
      includeProfiles: true,
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );

  const firstPage = suppliersByDistanceQuery.data?.pages[0];
  const shouldExpandSearch =
    suppliersByDistanceQuery.isFetched &&
    firstPage !== undefined &&
    firstPage.length < 15 &&
    supplierDistance < 100;
  if (shouldExpandSearch) {
    setSupplierDistance((distance) => distance + 20);
  }

  const suppliers = useMemo(
    () => suppliersByDistanceQuery.data?.pages.flatMap((page) => page) ?? [],
    [suppliersByDistanceQuery.data],
  );

  const isLoading = suppliersByDistanceQuery.isLoading;

  if (isLoading) return <SpinnerLegacy />;

  return (
    <div
      className={twMerge(
        "flex w-full gap-4 pb-4",
        suppliers.length > 3 && "overflow-x-scroll",
        suppliers.length < 3 && "justify-center",
      )}
    >
      {suppliers.map((supplier) => (
        <EducateConnectPotentialSupplierCard
          key={supplier.id}
          supplier={supplier}
        />
      ))}
    </div>
  );
};

const EducateConnectPotentialSupplierCard = ({
  supplier,
}: {
  supplier: SupplierDistanceResponseType[number];
}) => {
  const [modalActive, setModalActive] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  return (
    <>
      <Card
        key={supplier.id}
        className={twMerge(
          "bg-white",
          "relative w-[calc((100%/3)-(32px/3))] min-w-[290px] flex-none snap-center",
        )}
      >
        <CardImage
          imageUrl={supplier.imageUrl ?? ""}
          fallback="supplier-hero"
        />
        <div className={"flex flex-col gap-4 p-4"}>
          <div className="flex gap-3 rounded-lg bg-zinc-100 p-3">
            <ProfilePicture
              size={"large"}
              logoUrl={supplier.Business.logoUrl}
              name={supplier.Business.name}
              className="rounded-full border-2 border-white bg-white"
              fallback="supplier-badge"
            />
            <div className={"flex flex-col"}>
              <H5 className="overflow-hidden text-ellipsis whitespace-nowrap">
                {supplier.Business.name}
              </H5>
              <H6
                className={
                  "overflow-hidden text-ellipsis whitespace-nowrap text-gray-500"
                }
              >
                {[
                  supplier.Business.DeliveryAddress.city,
                  supplier.Business.DeliveryAddress.region,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </H6>
            </div>
          </div>
          <div className={"flex h-[24px] w-full gap-1 px-4"}>
            {supplier.supplierRunByProfileDetails &&
              supplier.supplierRunByProfileDetails.length > 0 && (
                <>
                  {supplier.supplierRunByProfileDetails.map((user, index) => (
                    <ProfilePicture
                      key={index}
                      logoUrl={user.profileImageUrl}
                      name={user.name}
                      size="small"
                      className="-ml-3 border border-white"
                    />
                  ))}
                  <H5 className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {supplier.supplierRunByString}
                  </H5>
                </>
              )}
          </div>
          <Button
            disabled={messageSent}
            className={twMerge(
              "w-full",
              messageSent &&
                "pointer-events-none border-zinc-300 bg-zinc-200 text-zinc-800 opacity-100",
            )}
            color="secondary"
            onClick={() => setModalActive(true)}
          >
            {messageSent ? "Message sent" : "I have an account"}
            <Icon icon={messageSent ? "CHECK_CIRCLE" : "ARROW_TOP_RIGHT"} />
          </Button>
        </div>
      </Card>
      <EducateConnectModal
        active={modalActive}
        supplier={supplier}
        onDismiss={() => setModalActive(false)}
        onSuccess={() => setMessageSent(true)}
      />
    </>
  );
};

const EducateConnectModal = ({
  active,
  onDismiss,
  onSuccess,
  supplier,
}: {
  active: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
  supplier: {
    id: number;
    Business: {
      name: string;
    };
  };
}) => {
  const connectionCreate = useSupplierRelationshipOnboarding();
  const { track } = useEvents();

  const form = useForm<ConnectWithSupplierFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      mailingListPermission: false,
    },
  });

  const formId = v4();
  const connectionComment = useWatch({
    control: form.control,
    name: "connectionComment",
  });

  const onSubmit = (connectionData: ConnectWithSupplierFormType) => {
    connectionCreate.mutate(
      {
        ...connectionData,
        SupplierId: supplier.id,
        initiator: "retailer",
      },
      {
        onSuccess: () => {
          track("Supplier Connection Requested", {
            source: "Supplier Connect Flow",
          });
          onSuccess();
          onDismiss();
        },
      },
    );
  };

  return (
    <>
      <Dialog active={active} onDismiss={onDismiss}>
        {connectionCreate.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(connectionCreate.error)?.response?.data
                .message ?? ""
            }
          />
        ) : null}
        <DialogNavigation
          title={`Great, let’s set up your account with ${supplier.Business.name}`}
          description={`Tell us who you usually order with at ${supplier.Business.name} and send them a message including any useful information you think will help get you set up quickly`}
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={onDismiss}
        />
        <DialogContent>
          <form className={"flex flex-col gap-2"} id={formId}>
            <div className="flex flex-col gap-1">
              <InputLabel>* Account manager name</InputLabel>
              <InputContainer className="mb-2">
                <InputText
                  {...form.register("accountManager", {
                    required: true,
                  })}
                />
              </InputContainer>

              <InputLabel>Additional notes</InputLabel>
              <InputContainer className="items-end">
                <InputTextArea
                  variant="legacyFixed"
                  className={"h-[100px]"}
                  {...form.register("connectionComment", {
                    required: false,
                  })}
                  maxLength={200}
                />
                <InputLengthIndicator
                  currentLength={connectionComment.length}
                  maxLength={200}
                />
              </InputContainer>

              <p className="text-zinc-400">
                For example, include information about any discounts or credit
                terms you may have
              </p>
            </div>
          </form>
        </DialogContent>
        <DialogCTA>
          <Button
            form={formId}
            onClick={form.handleSubmit((data) => {
              onSubmit(data);
            })}
            loading={connectionCreate.isPending}
          >
            Submit
          </Button>
        </DialogCTA>
      </Dialog>
    </>
  );
};
